import type { SavedRecipe } from '../types';
import { supabase } from './supabase';

function ensureClient() {
  if (!supabase) throw new Error('Weekstead backend is not configured.');
  return supabase;
}

export async function loadRecipesRemote(householdId: string): Promise<SavedRecipe[]> {
  const client = ensureClient();
  const recipes = await client.from('recipes').select('*').eq('household_id', householdId).eq('active', true).order('favorite', { ascending: false }).order('updated_at', { ascending: false });
  if (recipes.error) throw recipes.error;
  const rows = recipes.data ?? [];
  if (!rows.length) return [];
  const ingredients = await client.from('recipe_ingredients').select('*').eq('household_id', householdId).in('recipe_id', rows.map((r) => r.id)).order('created_at');
  if (ingredients.error) throw ingredients.error;
  const grouped = new Map<string, SavedRecipe['ingredients']>();
  for (const row of ingredients.data ?? []) {
    const list = grouped.get(row.recipe_id) ?? [];
    list.push({ name: row.ingredient_name, quantity: Number(row.quantity), unit: row.unit, optional: Boolean(row.optional) });
    grouped.set(row.recipe_id, list);
  }
  return rows.map((r) => ({
    id: r.id,
    sourceMealId: r.source_meal_id ?? null,
    name: r.name,
    servings: Number(r.servings),
    minutes: r.prep_minutes ?? 0,
    ingredients: grouped.get(r.id) ?? [],
    instructions: Array.isArray(r.instructions) ? r.instructions : [],
    notes: r.notes ?? null,
    sourceType: r.source_type,
    sourceName: r.source_name ?? null,
    sourceUrl: r.source_url ?? null,
    favorite: Boolean(r.favorite),
    timesMade: Number(r.times_made ?? 0),
    lastMadeAt: r.last_made_at ?? null,
    lastSuggestedAt: r.last_suggested_at ?? null,
    dismissedUntil: r.dismissed_until ?? null,
  }));
}

export async function saveMealAsRecipeRemote(mealId: string) {
  const client = ensureClient();
  const { data, error } = await client.rpc('save_meal_as_recipe', { meal_id_value: mealId });
  if (error) throw error;
  return String(data);
}

export async function startSavedRecipeRemote(recipeId: string) {
  const client = ensureClient();
  const { data, error } = await client.rpc('start_saved_recipe', { recipe_id_value: recipeId });
  if (error) throw error;
  return String(data);
}

export async function markRecipeSuggestedRemote(householdId: string, recipeId: string) {
  const client = ensureClient();
  const { error } = await client.from('recipes').update({ last_suggested_at: new Date().toISOString() }).eq('household_id', householdId).eq('id', recipeId);
  if (error) throw error;
}

export async function dismissRecipeSuggestionRemote(householdId: string, recipeId: string, days = 7) {
  const client = ensureClient();
  const until = new Date(Date.now() + days * 86400000).toISOString();
  const { error } = await client.from('recipes').update({ dismissed_until: until, last_suggested_at: new Date().toISOString() }).eq('household_id', householdId).eq('id', recipeId);
  if (error) throw error;
}

export async function archiveRecipeRemote(householdId: string, recipeId: string) {
  const client = ensureClient();
  const { error } = await client.from('recipes').update({ active: false }).eq('household_id', householdId).eq('id', recipeId);
  if (error) throw error;
}